import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY as empty, Observable, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LIST_TYPE } from '../../../../shared/constants/list-view/list-view.constant';
import { ListSummaryGroupings } from '../../../../shared/services/list-summary-groupings/list-summary-groupings.service';
import { ToggleBatchActions, UpdateSelectedStudentIds } from '../../../../store';
import { ListSearchComponent } from '../../list-search/list-search.component';
import { ImUser } from './../../../../shared/services/im-models/im-user';
import { MadLibGroupings } from './../../../../shared/services/mad-lib-services/mad-lib-groupings';
import { IMadlibOptions, MadLibHelpers } from './../../../../shared/services/mad-lib-services/mad-lib-helpers';
import { MadLibSorting } from './../../../../shared/services/mad-lib-services/mad-lib-sorting';
import { IListViewData } from './../../../../shared/typings/interfaces/list-view.interface';
import { IAttendanceListStudent } from './../../../../store/effects/projections/attendance';
import { DIMENSION_1, DIMENSION_2, GROUPING } from './attendance-mad-lib.constant';
import {
  IAttendanceDimension,
  IAttendanceDimensionCol,
  ISortData,
  TAttendanceDimension,
} from './attendance-mad-lib.interface';

@Component({
  selector: 'attendance-mad-lib',
  template: require('./attendance-mad-lib.component.html'),
})
export class AttendanceMadLib implements OnInit, OnDestroy {
  dimension1;
  dimension2;
  groupings;
  dimensions;
  filteredAttendanceMadLibs;
  madLibModels;
  filteredStudents$: Observable<IAttendanceListStudent[]>;
  columns;
  groupings$: Observable<any>;
  sortedGroupings$;
  listType = LIST_TYPE.ATTENDANCE;
  schoolType: string;
  sortState = { sortedColumn: null, sortOrder: null, dataType: null };
  madlibOptions: IMadlibOptions;
  selectedDimension1: string;
  selectedDimension2: string;
  selectedDimensions;
  batchEditMode: boolean;
  destroy$: Subject<boolean> = new Subject<boolean>();
  searchQuery: string;
  prevSearch: string;
  madlibStateSubscription: ISubscription;
  setPanelShadowStyle: boolean = false;

  @Input() currentSchool: any;
  @Input() currentUser: any;
  @Input() madlibOptions$: any;
  @Input() studentsWithFlagsAndSupports$;
  @Input() madlibState$;
  @Input() batchActionsMode$: Observable<any>;
  @ViewChild(ListSearchComponent) searchComponent: ListSearchComponent;

  constructor(
    private madLibGroupings: MadLibGroupings,
    private madLibHelpers: MadLibHelpers,
    private madLibSorting: MadLibSorting,
    private store: Store<any>,
    private listSummaryGroupings: ListSummaryGroupings,
    @Inject('$location') public $location: any,
    private imUser: ImUser,
  ) {}

  ngOnInit(): void {
    this.dimension1 = DIMENSION_1;
    this.dimensions = [this.dimension1, DIMENSION_2, GROUPING];

    this.selectedDimensions = {
      selectedDimension1: this.selectedDimension1,
      selectedDimension2: this.selectedDimension2,
    };

    this.studentsWithFlagsAndSupports$
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
      )
      .subscribe(() => this.updateGroupings());

    this.initializeInitialLoad();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  // todo: dry this (dvn)
  search(searchTerm) {
    const formattedSearchTerm = this.madLibHelpers.formatListSearchTerm(searchTerm);
    this.searchQuery = formattedSearchTerm;

    let searchStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlagsAndSupports$,
      this.madLibModels.Dimension1,
    );
    searchStudents$ = this.madLibHelpers.applySearchFilter(searchStudents$, formattedSearchTerm);
    this.groupings$ = this.getStudentGrouping(searchStudents$, this.madLibModels, this.columns, this.listType);

    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true,
      null,
      this.madLibModels,
    );
    this.clearBatchEditsInProgress();
    this.madLibHelpers.updateUrl(this.madLibModels, {
      sortKey,
      dataType,
      orderBy,
      search: this.searchQuery,
      list: LIST_TYPE.ATTENDANCE,
    });
  }

  clearSearch(): void {
    this.searchComponent.clearSearch();
  }

  filterStudents(newSort?: ISortData, newDimension?, lib?: TAttendanceDimension): void {
    const isDimensionUpdate = !!newDimension;

    if (isDimensionUpdate) this.updateListDataOnDimensionChange(newDimension, lib);
    this.clearBatchEditsInProgress();

    // always run this logic despite mode - sortUpdate, dimensionUpdate or initialLoad
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(newSort, newDimension, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });
  }

  getValidColumns(columns: IAttendanceDimensionCol[]): string[] {
    return columns.map(({ key }) => key);
  }

  // wrapper for run mode side-effects
  initializeInitialLoad(): void {
    this.madlibStateSubscription = this.madlibState$.pipe(take(1)).subscribe(madlibState => {
      this.filteredAttendanceMadLibs = madlibState.filteredMadLib;
      this.madLibModels = madlibState.madLibModel;
    });

    this.columns = this.madLibHelpers.getListColumns(this.madLibModels);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlagsAndSupports$,
      this.madLibModels.Dimension1,
    );
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModels, this.columns, this.listType);

    // always run this logic despite mode - sortUpdate, dimensionUpdate or initialLoad
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });

    const urlValues = this.$location.search();
    this.prevSearch = this.madLibHelpers.getSearchTerm(urlValues, LIST_TYPE.ATTENDANCE);
  }

  updateGroupings(): void {
    this.batchActionsMode$
      .pipe(
        switchMap(mode => {
          return mode ? empty : this.madlibState$;
        }),
        tap((madlibState: any) => {
          if (madlibState) {
            this.filteredAttendanceMadLibs = madlibState.filteredMadLib;
            this.madLibModels = this.madLibModels;
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.filteredStudents$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModels, this.columns, this.listType);
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(true, true, validColumns);
    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );
  }
  // wrapper for run mode side-effects
  updateListDataOnDimensionChange(newDimension, newDimensionNum: TAttendanceDimension): void {
    this.sortState = { sortedColumn: null, sortOrder: null, dataType: null };
    this.madLibModels = this.madLibHelpers.updateMadLibModel(this.madLibModels, newDimensionNum, newDimension);
    this.columns = this.madLibHelpers.getListColumns(this.madLibModels);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlagsAndSupports$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModels, this.columns, this.listType);
  }

  // called on change of dimension3/grouping pill
  getStudentGrouping(
    filteredStudents$,
    madLibModels,
    columns: IAttendanceDimensionCol,
    listType: string,
  ): Observable<Partial<IListViewData>> {
    const {
      Groupings: { key },
    } = madLibModels;
    let groupings$: Observable<Partial<IListViewData>>;
    switch (key) {
      case 'TREND':
        groupings$ = this.madLibGroupings.getTrendGroupings(filteredStudents$, madLibModels, columns, listType);
        break;
      case 'RISK_GROUP':
        groupings$ = this.madLibGroupings.getAttRiskGroupings(filteredStudents$, madLibModels, columns, listType);
        break;
      case 'SUPPORT':
        groupings$ = this.madLibGroupings.getSupportGroupings(filteredStudents$, columns, listType, madLibModels);
        break;
      case 'FLAG':
        groupings$ = this.madLibGroupings.getFlagGroupings(filteredStudents$, columns, listType);
        break;
      case 'ADMIT':
        groupings$ = this.madLibGroupings.getAdmitStatusGroupings(filteredStudents$, madLibModels, columns, listType);
        break;
      default:
        groupings$ = null;
        break;
    }
    this.listSummaryGroupings.setGroupings(madLibModels, groupings$);
    return groupings$;
  }

  isOptionActive(lib: IAttendanceDimension, option: string): boolean {
    if (lib.madLib === 'Dimension1') return this.selectedDimension1 === option;
    if (lib.madLib === 'Dimension2') return this.selectedDimension2 === option;
    return;
  }

  clearBatchEditsInProgress(): void {
    this.batchEditMode = false;
    this.store.dispatch(new ToggleBatchActions(this.batchEditMode));
    this.store.dispatch(new UpdateSelectedStudentIds([]));
  }
}
